import React, {useEffect, useState} from 'react';
import {Form, FormGroup, Label, Input, InputGroup, Button, ButtonGroup, Collapse} from 'reactstrap';
import TierSwitch from '../../components/TierSwitch';
import CountrySelect from '../../components/CountrySelect';
import OrganizationSelect from '../../components/OrganizationSelect';
import Countries from './countries.json';
import {apiRequest} from '../../utils/api-request';
import {useDispatch, useSelector} from 'react-redux';
import { DatePicker } from '../../components/DateTime';
import ToggleSwitch from '../../components/ToggleSwitch';
import SelectDistributor from './SelectDistributor';
import VersionPricing from './VersionPricing';
import BadgeSelect from '../../components/BadgeSelect';
import {fetchDistributors} from '../../slices/distributors.slice';
import JsonDisplay from '../../components/JsonDisplay'
import useSavePrompt from '../../utils/useSavePrompt';
import SavePrompt from '../../components/SavePrompt';


const getDistributorId = (distributor) => {
    if (!distributor) return null;
    return typeof distributor === 'object' ? distributor._id : distributor;
};

const VersionForm = ({ versionData, originalData, handleSubmit, handleChange, handleCancel, toggleCountriesModal, countriesModal, toggleOrganizationsModal, organizationsModal, organizations, hideButtons, onDirtyChange }) => {
    const isEditing = !!versionData._id;
    const [assetFetching, setAssetFetching] = useState(false);
    const { blocker, isDirty } = useSavePrompt(originalData, versionData);

    useEffect(() => {
        onDirtyChange?.(isDirty);
    }, [isDirty, onDirtyChange]);
    const [assetDetails, setAssetDetails] = useState(versionData.assetDetails);
    useEffect(() => {
        setAssetDetails(versionData.assetDetails);
    }, [versionData.assetDetails]);
    const [assetDetailsError, setAssetDetailsError] = useState(false);
    const { list: distributors } = useSelector(state => state.distributors);
    const [distributorModal, setDistributorModal] = useState(false);
    const [assetDetailsOpen, setAssetDetailsOpen] = useState(false);
    const [hirePeriod, setHirePeriod] = useState({
        defaultHirePeriod:versionData.defaultHirePeriod,
        hirePeriodType: versionData.hirePeriodType
    })
    useEffect(() => {
        setHirePeriod({
            defaultHirePeriod: versionData.defaultHirePeriod,
            hirePeriodType: versionData.hirePeriodType
        });
    }, [versionData.defaultHirePeriod, versionData.hirePeriodType]);
    const dispatch = useDispatch();

    const handleFetchAsset = async e => {
        e.preventDefault();
        setAssetFetching(true);
        setAssetDetailsError(false);
        try {
            const details = await apiRequest(`/external/fetch-asset/${versionData.uuid}`);
            setAssetDetails(details);
            setAssetDetailsOpen(true);
            handleChange({
                target: {
                    name: 'assetDetails',
                    value: details,
                },
            });
        } catch (e) {
            setAssetDetailsError(true);
            console.error(e);
        } finally {
            setAssetFetching(false);
        }
    };

    const isValidUUID = () => {
        const uuidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/;
        return uuidRegex.test(versionData.uuid);
    }

    const handleDistributorSelect = distributor => {
        handleChange({target: {name: 'distributor', value: distributor._id}});
        toggleDistributorModal();
    };

    const toggleDistributorModal = () => setDistributorModal(!distributorModal);

    const changeContentRestrictions = value => {
        handleChange({ target: { name: 'contentRestrictions', value }});
    };

    const handleChangeHirePeriod =(e)=>{
        const { name, value } = e.target;
        handleChange({target:{name:name, value:value}})
        setHirePeriod({...hirePeriod, [name]:value})
    }

    const fetchHirePeriod =()=>{
        const distributorId = getDistributorId(versionData.distributor);
        const distributor = distributors?.find(({ _id }) => _id === distributorId);
        if (!distributor) return;
        handleChange([
            {target:{name: 'defaultHirePeriod',value: distributor.defaultHirePeriod}},
            {target:{ name: 'hirePeriodType',value: distributor.hirePeriodType}}
        ]);
        setHirePeriod({defaultHirePeriod: distributor.defaultHirePeriod, hirePeriodType: distributor.hirePeriodType})
    }

    useEffect(() => {
        dispatch(fetchDistributors());
    }, [dispatch]);

    return (
        <Form id="version-form" onSubmit={handleSubmit} className="w-100">
            <div className="bg-broken p-3 mb-3">
                <SelectDistributor
                    isOpen={distributorModal}
                    toggle={toggleDistributorModal}
                    distributors={distributors}
                    onSelect={handleDistributorSelect}
                    selectedDistributor={getDistributorId(versionData.distributor)}
                />
            </div>

            <FormGroup>
                <Label for="name">Name</Label>
                <Input type="text" name="name" id="name" value={versionData.name} onChange={handleChange} required/>
            </FormGroup>

            <FormGroup>
                <Label for="uuid">Portal UUID</Label>
                <InputGroup>
                    <Input type="text" name="uuid" id="uuid" value={versionData.uuid} onChange={handleChange} invalid={assetDetailsError} />
                    <Button color="primary" onClick={handleFetchAsset} disabled={!isValidUUID()}>
                        {isValidUUID() ? (
                            <span className={`fas fa-refresh me-2 ${assetFetching && 'fa-spin'}`}/>
                        ) : (
                            <span className="fas fa-warning me-2"/>
                        )}

                        {isValidUUID() ? 'Fetch Asset Details' : 'Invalid UUID'}
                    </Button>
                </InputGroup>

                <div
                    className={`d-flex align-items-center mt-2 p-2 bg-primary bg-opacity-25 cursor-pointer rounded${assetDetailsOpen ? '-top': ''}`}
                    style={{ cursor: 'pointer' }}
                    onClick={() => setAssetDetailsOpen(!assetDetailsOpen)}
                >
                    <span className={`fas fa-chevron-${assetDetailsOpen ? 'up' : 'down'} me-2`} />
                    <span className="flex-grow-1">
                        Asset Details
                        <span className="small ms-2 text-white-50">
                            {assetDetails
                                ? `(${Object.keys(assetDetails).length} properties)`
                                : <><span className="fas fa-warning text-warning me-1" />Not fetched yet</>
                            }
                        </span>
                    </span>

                </div>
                <Collapse isOpen={assetDetailsOpen}>
                    <div className="p-3 bg-primary bg-opacity-10 rounded-bottom">
                        {assetDetails ? (
                            <JsonDisplay
                                data={assetDetails}
                                collapsible={false}
                                editable
                                onChange={updated => {
                                    setAssetDetails(updated);
                                    handleChange({target: {name: 'assetDetails', value: updated}});
                                }}
                            />
                        ) : (
                            <span className="text-white">No asset details fetched yet</span>
                        )}
                    </div>
                </Collapse>
            </FormGroup>

            <div className="d-flex">
                <FormGroup>
                    <Label className="me-2 d-block">Tier(s)</Label>
                    <TierSwitch value={versionData.tier} onChange={handleChange} multiple/>
                </FormGroup>

                <FormGroup className="ms-md-3">
                    <Label>Is Movie on Request?</Label>
                    <div className="pt-1">
                        <ToggleSwitch
                            isOn={versionData.isMovieOnRequest}
                            onChange={value => handleChange({target: {name: 'isMovieOnRequest', value}})}
                        />
                    </div>
                </FormGroup>

                <FormGroup className="ms-md-3">
                    <Label>BAC Aurora?</Label>
                    <div className="pt-1">
                        <ToggleSwitch
                            isOn={versionData.isAurora}
                            onChange={value => handleChange({target: {name: 'isAurora', value}})}
                        />
                    </div>
                </FormGroup>
            </div>

            <FormGroup>
                <Label>Content Restrictions</Label>
                <BadgeSelect
                    defaultList={[6, 9, 12, 14, 16, 18]}
                    onChange={changeContentRestrictions}
                    values={versionData.contentRestrictions}
                    multiple={false}
                    append="+"
                />
            </FormGroup>

            <FormGroup>
                <Label for="countries">Region availability</Label>
                <CountrySelect
                    name="countries"
                    value={versionData.countries}
                    countries={Countries.regions}
                    onChange={values => handleChange({target: {name: 'countries', value: values}})}
                    placeholder="Select countries"
                    modal={countriesModal}
                    toggle={toggleCountriesModal}
                />
            </FormGroup>

            <FormGroup>
                <Label for="countries">Organization availability</Label>
                <OrganizationSelect
                    name="organizations"
                    value={organizations.filter((org) => {
                        const ids = versionData.availableForOrganizations?.map(o => typeof o === 'object' ? o._id : o) || [];
                        return ids.includes(org._id);
                    })}
                    organizations={organizations}
                    addLogo
                    useId
                    onChange={values => handleChange({target: {name: 'availableForOrganizations', value: values}})}
                    placeholder="Select organizations"
                    modal={organizationsModal}
                    toggle={toggleOrganizationsModal}
                />
            </FormGroup>

            <div className="d-flex">
                <FormGroup>
                    <Label>Release Date</Label>
                    <DatePicker
                        value={versionData.releaseDate}
                        onChange={(date) => handleChange({target: {name: 'releaseDate', value: date?.toISOString()}})}
                        placeholder="After"
                        clearable
                    />
                </FormGroup>
            </div>

            <VersionPricing
                versionData={versionData}
                distributor={distributors?.find(({ _id }) => _id === getDistributorId(versionData.distributor))}
                handleChange={handleChange}
            />

            <FormGroup>
                <Label>Hire Period</Label>
                <Button color="link" onClick={fetchHirePeriod} className="text-decoration-none" style={{ fontSize: '12px'}}>
                                    <span className="fas fa-refresh me-2" />
                                    Fetch from distributor
                                </Button>
                <div className='d-flex'>
                    <Input
                        value={hirePeriod.defaultHirePeriod ?? ''}
                        name="defaultHirePeriod"
                        type='number'
                        onChange={handleChangeHirePeriod}/>

                    <select className="form-select-small ms-3" value={hirePeriod.hirePeriodType} name='hirePeriodType' onChange={handleChangeHirePeriod}>
                        <option value="days">Days</option>
                        <option value="hours">Hours</option>
                    </select>
                </div>
            </FormGroup>

            {!hideButtons && (
                <div className="d-flex float-end my-3">
                    <ButtonGroup>
                        <Button color="primary" outline onClick={handleCancel}>Cancel</Button>
                        <Button color="primary" type="submit" disabled={!versionData.distributor || (versionData.uuid && (!isValidUUID() || !versionData.assetDetails || assetDetailsError))}>
                            {isDirty && <span className="unsaved-dot" />}
                            <span className="fas fa-save me-2"/>
                            {isEditing ? 'Save Changes' : 'Create Version'}
                        </Button>
                    </ButtonGroup>
                </div>
            )}
            <SavePrompt blocker={blocker} />
        </Form>
    );
};

export default VersionForm;
